import {
  readTimer,
  timerKey,
  type BehaviorTimerData,
  type TimerCommand,
  type TimerPacket,
  type TimerSnapshot,
} from "./protocol.js";

export type BehaviorTimerTransport = {
  request(): Promise<TimerPacket>;
  command(command: TimerCommand): Promise<TimerPacket>;
  listen(
    receive: (packet: TimerPacket) => void,
    connection: (connected: boolean) => void,
  ): () => void;
};
/** One client clock and one refresh loop per page, shared by all Behavior instances. */
export class BehaviorTimerClient {
  private transport?: BehaviorTimerTransport;
  private entries = new Map<string, { timer: TimerSnapshot; anchor: number }>();
  private listeners = new Map<() => void, string>();
  private cache = new Map<string, BehaviorTimerData>();
  private stop?: () => void;
  private tick?: ReturnType<typeof setInterval>;
  private resync?: ReturnType<typeof setInterval>;
  private session?: string;
  private sequence = -1;
  private serverClockOffset?: number;
  private pending = Promise.resolve();
  connected = false;
  constructor(private now: () => number = () => performance.now()) {}
  configure(transport: BehaviorTimerTransport): () => void {
    this.stop?.();
    this.transport = transport;
    this.stop = transport.listen(
      (p) => this.receive(p),
      (connected) => {
        if (this.connected === connected) return;
        this.connected = connected;
        this.notify();
        if (connected && this.listeners.size)
          void this.refresh().catch(() => {});
      },
    );
    if (this.listeners.size) this.start();
    return () => {
      this.stop?.();
      this.stop = undefined;
      this.transport = undefined;
      this.connected = false;
      this.notify();
      this.clearLoops();
    };
  }
  receive(packet: TimerPacket, latencyMs?: number): void {
    if (packet.session && packet.session !== this.session) {
      this.session = packet.session;
      this.sequence = -1;
      this.entries.clear();
      this.serverClockOffset = undefined;
    }
    if (latencyMs !== undefined && packet.sentAtMs !== undefined)
      this.serverClockOffset = this.now() - latencyMs - packet.sentAtMs;
    if (packet.sequence !== undefined && packet.sequence <= this.sequence)
      return;
    this.sequence = packet.sequence ?? this.sequence;
    if (packet.replace) this.entries.clear();
    const anchor =
      packet.sampledAtMs !== undefined && packet.sentAtMs !== undefined
        ? packet.sampledAtMs +
          (this.serverClockOffset ?? this.now() - packet.sentAtMs)
        : this.now() - Math.max(0, latencyMs ?? 0);
    for (const timer of packet.timers)
      this.entries.set(timerKey(timer.scope, timer.reference), {
        timer,
        anchor,
      });
    for (const key of packet.removed ?? [])
      this.entries.delete(timerKey(key.scope, key.reference));
    this.notify();
  }
  private notify() {
    this.cache.clear();
    for (const listener of this.listeners.keys()) listener();
  }
  data(scope: string, fresh = false): BehaviorTimerData {
    const cached = this.cache.get(scope);
    if (cached && !fresh) return cached;
    const data: BehaviorTimerData = Object.create(null);
    for (const { timer, anchor } of this.entries.values())
      if (timer.scope === scope)
        data[timer.reference] = {
          ...readTimer(timer, this.now() - anchor),
          connected: this.connected,
        };
    if (!fresh) this.cache.set(scope, data);
    return data;
  }
  subscribe(scope: string, listener: () => void): () => void {
    this.cache.delete(scope);
    this.listeners.set(listener, scope);
    this.start();
    return () => {
      this.listeners.delete(listener);
      if (!this.listeners.size) this.clearLoops();
    };
  }
  private clearLoops() {
    if (this.tick) clearInterval(this.tick);
    if (this.resync) clearInterval(this.resync);
    this.tick = undefined;
    this.resync = undefined;
  }
  private start() {
    if (this.tick) return;
    void this.refresh().catch(() => {});
    this.tick = setInterval(() => {
      const scopes = new Set(
        [...this.entries.values()]
          .filter((e) => readTimer(e.timer, this.now() - e.anchor).running)
          .map((e) => e.timer.scope),
      );
      // Include a countdown reaching zero on this tick, then stop repainting it.
      const dirty = new Set(
        [...this.listeners.values()].filter(
          (scope) =>
            scopes.has(scope) ||
            Object.values(this.cache.get(scope) ?? {}).some((t) => t.running),
        ),
      );
      for (const scope of dirty) this.cache.delete(scope);
      for (const [listener, scope] of this.listeners)
        if (dirty.has(scope)) listener();
    }, 25);
    this.resync = setInterval(() => {
      void this.refresh().catch(() => {});
    }, 10000);
  }
  async refresh(): Promise<void> {
    if (!this.transport) return;
    const sent = this.now();
    const packet = await this.transport.request();
    this.receive(packet, (this.now() - sent) / 2);
  }
  command(command: TimerCommand): Promise<void> {
    const execute = async () => {
      if (!this.transport) throw new Error("BEHAVIOR_TIMER_HOST_UNAVAILABLE");
      const sent = this.now();
      this.receive(
        await this.transport.command(command),
        (this.now() - sent) / 2,
      );
    };
    const result = this.pending.then(execute);
    this.pending = result.catch(() => {});
    return result;
  }
}
export const behaviorTimerClient = new BehaviorTimerClient();
